import "../compliance/compliance.css";
import "./uniform.css";
import { useEffect, useState } from "react";
import { Layout, Table, Tabs } from "antd";
import { useApolloClient, useQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { FullScreenGrid } from "@src/components/fullScreenGrid/fullScreenGrid";
import { GridSearch, GridSelect, MonthPicker, PagedGrid, TableActions, useDebounced } from "@src/components/fullScreenGrid/gridToolbar";
import { getUniformComplianceQuery, getUniformIssuesQuery, getUniformItemsQuery, getUniformRequestsQuery, getUniformSummaryQuery } from "@src/util/query/uniform.query";
import { LogIssueDrawer, MODULE_STANDARD, STATUS, downloadCsv, reasonLabel, ukDate } from "./uniformParts";
import { COMPLIANCE_VIEWS, CompliancePanel, ReceiptModal, complianceCount, exportCompliance } from "./uniformRecords";
import { UniformIssuesTable, UniformRequestsTable } from "./uniformTables";

const { Content } = Layout;
const STATUS_FILTERS = [
    { value: "open", label: "Waiting for approval or issue" },
    { value: "pending", label: "Pending approval" },
    { value: "approved", label: "Approved, not issued yet" },
    { value: "issued", label: "Issued" },
    { value: "rejected", label: "Declined" },
    { value: "cancelled", label: "Cancelled" },
    { value: "", label: "Everything" },
];
const day = (d) => d.format("YYYY-MM-DD");
const Num = ({ value }) => (value ? <span>{value}</span> : <span className="uni-zero">0</span>);
const matches = (search, ...values) => !search.trim() || values.join(" ").toLowerCase().includes(search.trim().toLowerCase());

/** The grid every module page uses around its table */
const Grid = ({ children }) => (
    <div className="tableView">
        <Layout className="bg-white all-module-grid">
            <Content className="site-layout">{children}</Content>
        </Layout>
    </div>
);

/**
 * The dashboard's Uniform Requests tile opens this, full screen: who asked for what (approve /
 * decline / issue, the name opens their profile), how much of each item and size has been asked
 * for and issued in a month, and the log of uniform issued.
 */
export const UniformRequestsModal = ({ visible, onClose, onChanged, liveTick }) => {
    const { authenticatedUserDetail } = useSelector((state) => state.userAuthReducer);
    const permission = authenticatedUserDetail?.permission?.Uniform;
    const client = useApolloClient();
    const canCreate = permission?.access !== false && permission?.create !== false;
    const [tab, setTab] = useState("requests");
    const [status, setStatus] = useState("open");
    const [search, setSearch] = useState("");
    const [month, setMonth] = useState(dayjs().startOf("month"));
    const [issueItem, setIssueItem] = useState("");
    const [logOpen, setLogOpen] = useState(false);
    const [complianceView, setComplianceView] = useState("missingRequired");
    const [receiptId, setReceiptId] = useState(null);
    const [requestPage, setRequestPage] = useState({ page: 1, pageSize: 20 });
    const [issuePage, setIssuePage] = useState({ page: 1, pageSize: 20 });
    // the server searches (every page); a filter change goes back to page 1
    const searchText = useDebounced(search.trim());
    useEffect(() => { setRequestPage((p) => ({ ...p, page: 1 })); }, [searchText, status]);
    useEffect(() => { setIssuePage((p) => ({ ...p, page: 1 })); }, [searchText, issueItem, month]);
    const from = day(month.startOf("month"));
    const to = day(month.endOf("month"));

    const { data: requestData, loading: requestsLoading, refetch: refetchRequests } = useQuery(getUniformRequestsQuery, {
        variables: { filter: { status: status === "open" ? ["pending", "approved"] : status ? [status] : null, search: searchText || null, ...requestPage } },
        skip: !visible || tab !== "requests", fetchPolicy: "network-only",
    });
    // the numbers on top: what's waiting, whatever the list is filtered to (the counts only)
    const { data: pendingData, refetch: refetchPending } = useQuery(getUniformRequestsQuery, {
        variables: { filter: { status: ["pending"], pageSize: 1 } }, skip: !visible, fetchPolicy: "network-only",
    });
    const { data: approvedData, refetch: refetchApproved } = useQuery(getUniformRequestsQuery, {
        variables: { filter: { status: ["approved"], pageSize: 1 } }, skip: !visible, fetchPolicy: "network-only",
    });
    const { data: issueData, loading: issuesLoading, refetch: refetchIssues } = useQuery(getUniformIssuesQuery, {
        variables: { filter: { from, to, itemId: issueItem || null, search: searchText || null, ...issuePage } },
        skip: !visible || tab !== "issued", fetchPolicy: "network-only",
    });
    const { data: summaryData, loading: summaryLoading, refetch: refetchSummary } = useQuery(getUniformSummaryQuery, {
        variables: { from, to },
        skip: !visible || tab !== "summary", fetchPolicy: "network-only",
    });
    const { data: complianceData, loading: complianceLoading, refetch: refetchCompliance } = useQuery(getUniformComplianceQuery, {
        skip: !visible || tab !== "compliance", fetchPolicy: "network-only",
    });
    const compliance = complianceData?.getUniformCompliance?.response;
    const { data: itemData } = useQuery(getUniformItemsQuery, { variables: { includeArchived: true }, skip: !visible || tab !== "issued", fetchPolicy: "network-only" });

    const requestResult = requestData?.getUniformRequests?.response;
    const requests = requestResult?.rows || [];
    const pending = pendingData?.getUniformRequests?.response?.totalCount || 0;
    const toIssue = approvedData?.getUniformRequests?.response?.totalCount || 0;
    const issueResult = issueData?.getUniformIssues?.response;
    const issues = issueResult?.rows || [];
    const summary = summaryData?.getUniformSummary?.response;
    const summaryRows = (summary?.rows || []).filter((r) => matches(search, r.itemName, r.size));
    const items = itemData?.getUniformItems?.response || [];

    const refresh = () => {
        refetchPending();
        refetchApproved();
        if (tab === "requests") refetchRequests();
        if (tab === "issued") refetchIssues();
        if (tab === "summary") refetchSummary();
        if (tab === "compliance") refetchCompliance();
        onChanged?.();
    };

    // exports take every row the filters match, not just the page on screen
    const allPages = async (query, key, filter) => {
        const rows = [];
        for (let page = 1; page <= 200; page++) {
            const { data } = await client.query({ query, variables: { filter: { ...filter, page, pageSize: 500 } }, fetchPolicy: "network-only" });
            const result = data?.[key]?.response;
            rows.push(...(result?.rows || []));
            if (!result || rows.length >= result.totalCount || !result.rows?.length) break;
        }
        return rows;
    };
    // a request added or changed elsewhere (the app, another manager) - re-read what's open
    useEffect(() => {
        if (!visible || !liveTick) return;
        refetchPending(); refetchApproved();
        if (tab === "requests") refetchRequests();
        if (tab === "issued") refetchIssues();
        if (tab === "summary") refetchSummary();
    }, [liveTick]);

    const exportRequests = async () => downloadCsv("uniform-requests.csv",
        ["Asked", "Employee", "Items", "Reason", "Note", "Status", "Decided by", "Issued on", "Issued"],
        (await allPages(getUniformRequestsQuery, "getUniformRequests", { status: status === "open" ? ["pending", "approved"] : status ? [status] : null, search: searchText || null })).map((r) => [ukDate(r.createdAt), r.employeeName, r.summary, reasonLabel(r.reason), r.note || "", STATUS[r.status]?.label || r.status, r.decidedByName, r.issuedDate ? ukDate(r.issuedDate) : "", r.issuedSummary || ""]));
    const exportIssues = async () => downloadCsv(`uniform-issued-${month.format("YYYY-MM")}.csv`,
        ["Date", "Employee", "Item", "Size", "Quantity", "Issued by", "From a request", "Note"],
        (await allPages(getUniformIssuesQuery, "getUniformIssues", { from, to, itemId: issueItem || null, search: searchText || null })).map((r) => [ukDate(r.issuedDate), r.employeeName, r.itemName, r.size || "One size", r.quantity, r.issuedByName, r.requestId ? "Yes" : "No", r.note || ""]));
    const exportSummary = () => downloadCsv(`uniform-summary-${month.format("YYYY-MM")}.csv`,
        ["Item", "Size", "Requested", "Pending approval", "Approved, not issued", "Declined", "Issued"],
        summaryRows.map((r) => [r.itemName, r.size || "One size", r.requested, r.pending, r.approved, r.rejected, r.issued]));

    const stats = tab === "compliance" ? COMPLIANCE_VIEWS.map((v) => ({ label: v.label, value: compliance ? complianceCount(compliance.counts, v) : "-" }))
        : tab === "summary" ? (summary ? [
        { label: "Requests", value: summary.counts.requests },
        { label: "Employees asking", value: summary.counts.employeesRequesting },
        { label: "Items requested", value: summary.totals.requested },
        { label: "Items issued", value: summary.totals.issued },
    ] : []) : tab === "issued" ? [
        { label: "Entries", value: issueResult?.totalCount || 0 },
        { label: "Items issued", value: issueResult?.totals?.items || 0 },
        { label: "Employees", value: issueResult?.totals?.employees || 0 },
    ] : [
        { label: "Pending approval", value: pending },
        { label: "To issue", value: toIssue },
    ];

    return (
        <FullScreenGrid
            visible={visible}
            onClose={onClose}
            title="Uniform Requests"
            subtitle={tab === "requests" ? `${requestResult?.totalCount || 0} records` : tab === "issued" ? `${issueResult?.totalCount || 0} records` : tab === "compliance" ? MODULE_STANDARD : month.format("MMMM YYYY")}
            intro={tab === "compliance"
                ? "Where your officers stand against the uniform standards: standard issue and hi-vis held, renewals, signed receipts and company kit recovered from leavers."
                : "Uniform officers have asked for in the app - approve or decline it, then mark it issued when it's handed over."}
            stats={stats}
        >
            <div className="setting-body-inner grid-tabs" style={{ padding: 0, marginTop: 8 }}>
                <Tabs activeKey={tab} onChange={(key) => { setTab(key); setSearch(""); }} items={[
                    { key: "requests", label: "Requests" },
                    { key: "compliance", label: "Compliance" },
                    // { key: "summary", label: "Summary" },
                    // { key: "issued", label: "Issued Log" },
                ]} />
            </div>

            {tab === "requests" ? (
                <>
                    <div className="fsg-toolbar">
                        <GridSearch value={search} onChange={setSearch} placeholder="Search by Employee or Item..." />
                        <div className="fsg-toolbar-actions">
                            <GridSelect value={status} onChange={setStatus} options={STATUS_FILTERS} width={280} />
                            <TableActions actions={[{ key: "export", label: "Export CSV", onClick: exportRequests, disabled: !requests.length }]} />
                        </div>
                    </div>
                    <PagedGrid page={requestPage.page} pageSize={requestPage.pageSize} total={requestResult?.totalCount || 0}
                        onChange={(page, pageSize) => setRequestPage({ page, pageSize })}>
                        <UniformRequestsTable requests={requests} loading={requestsLoading} permission={permission} onChanged={refresh} pagination={false}
                            emptyText={status === "open" && !searchText ? "Nothing waiting. Officers ask for uniform from the app." : "No uniform requests found"} />
                    </PagedGrid>
                </>
            ) : tab === "compliance" ? (
                <>
                    <div className="fsg-toolbar">
                        <GridSearch value={search} onChange={setSearch} placeholder="Search by Employee or Item..." />
                        <div className="fsg-toolbar-actions">
                            <GridSelect value={complianceView} onChange={setComplianceView} width={300}
                                options={COMPLIANCE_VIEWS.map((v) => ({ value: v.key, label: `${v.label}${compliance ? ` (${complianceCount(compliance.counts, v)})` : ""}` }))} />
                            <TableActions actions={[{ key: "export", label: "Export CSV", onClick: () => exportCompliance(compliance, complianceView), disabled: !compliance?.[complianceView]?.length }]} />
                        </div>
                    </div>
                    <CompliancePanel compliance={compliance} loading={complianceLoading} view={complianceView} onOpenReceipt={setReceiptId} search={search} />
                </>
            ) : tab === "summary" ? (
                <>
                    <div className="fsg-toolbar">
                        <GridSearch value={search} onChange={setSearch} placeholder="Search by Item or Size..." />
                        <div className="fsg-toolbar-actions">
                            <MonthPicker month={month} onChange={setMonth} />
                            <TableActions actions={[{ key: "export", label: "Export CSV", onClick: exportSummary, disabled: !summaryRows.length }]} />
                        </div>
                    </div>
                    <Grid>
                        <Table
                            className="curvedTable uni-summary-table"
                            loading={summaryLoading}
                            columns={[
                                { title: "Item", dataIndex: "itemName" },
                                { title: "Size", dataIndex: "size", render: (v) => v || <span className="cmp-muted">One size</span> },
                                { title: "Requested", dataIndex: "requested", render: (v) => <strong><Num value={v} /></strong> },
                                { title: "Pending approval", dataIndex: "pending", render: (v) => <Num value={v} /> },
                                { title: "Approved, not issued", dataIndex: "approved", render: (v) => <Num value={v} /> },
                                { title: "Declined", dataIndex: "rejected", render: (v) => <Num value={v} /> },
                                { title: "Issued", dataIndex: "issued", render: (v) => <strong><Num value={v} /></strong> },
                            ]}
                            dataSource={summaryRows.map((row) => ({ ...row, key: `${row.itemId}|${row.size}` }))}
                            pagination={false}
                            summary={() => summaryRows.length ? (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={2}><strong>Total</strong></Table.Summary.Cell>
                                    <Table.Summary.Cell index={2}><strong>{summaryRows.reduce((s, r) => s + r.requested, 0)}</strong></Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>{summaryRows.reduce((s, r) => s + r.pending, 0)}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={4}>{summaryRows.reduce((s, r) => s + r.approved, 0)}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={5}>{summaryRows.reduce((s, r) => s + r.rejected, 0)}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={6}><strong>{summaryRows.reduce((s, r) => s + r.issued, 0)}</strong></Table.Summary.Cell>
                                </Table.Summary.Row>
                            ) : null}
                            locale={{ emptyText: `No uniform requested or issued in ${month.format("MMMM YYYY")}` }}
                        />
                    </Grid>
                </>
            ) : (
                <>
                    <div className="fsg-toolbar">
                        <GridSearch value={search} onChange={setSearch} placeholder="Search by Employee or Item..." />
                        <div className="fsg-toolbar-actions">
                            <MonthPicker month={month} onChange={setMonth} />
                            <GridSelect value={issueItem} onChange={setIssueItem} width={200}
                                options={[{ value: "", label: "All items" }, ...items.map((i) => ({ value: i._id, label: i.isArchived ? `${i.name} (archived)` : i.name }))]} />
                            <TableActions actions={[
                                canCreate && { key: "log", label: "Log Uniform Issued", onClick: () => setLogOpen(true) },
                                { key: "export", label: "Export CSV", onClick: exportIssues, disabled: !issues.length },
                            ].filter(Boolean)} />
                        </div>
                    </div>
                    <PagedGrid page={issuePage.page} pageSize={issuePage.pageSize} total={issueResult?.totalCount || 0}
                        onChange={(page, pageSize) => setIssuePage({ page, pageSize })}>
                        <UniformIssuesTable issues={issues} loading={issuesLoading} permission={permission} onChanged={refresh} pagination={false}
                            emptyText={`No uniform issued in ${month.format("MMMM YYYY")}`} />
                    </PagedGrid>
                </>
            )}
            <LogIssueDrawer visible={logOpen} onClose={() => setLogOpen(false)} onSaved={refresh} />
            <ReceiptModal receiptId={receiptId} permission={permission} onClose={() => setReceiptId(null)} onChanged={refresh} />
        </FullScreenGrid>
    );
};
